import type { LinkedPerson, OrganizationWithPeople } from '../types';
import { formatSportLabel } from './sportLabel';

export type RollupLevel = 'TEAM' | 'SPORT' | 'ORG';

export type RolledUpDisplayLink =
  | {
      scope: 'ORG';
      items: Array<{ role: string | null }>;
    }
  | {
      scope: 'SPORT';
      sportId: string;
      sportName: string;       // gender-prefixed display name, e.g. "Boys Football"
      items: Array<{
        role: string | null;
        teamLevels?: string[]; // present iff link is team-level
      }>;
    };

export interface RolledUpPerson {
  person: LinkedPerson['person'];
  isPrimary: boolean;
  level: RollupLevel;
  displayLinks: RolledUpDisplayLink[];
  firstTeamLink?: { teamId: string; sportId: string };
  isFormer: boolean;          // true = every link for this person at this org is end-dated
  lastSeasonYear: number | null; // max endDate year across links; null when current
}

interface MutableAccum {
  person: LinkedPerson['person'];
  isPrimary: boolean;
  hasOrgLink: boolean;
  hasSportLink: boolean;
  hasTeamLink: boolean;
  orgRoles: Array<{ role: string | null }>;
  // Map sportId -> per-sport accumulator
  sports: Map<string, {
    sportName: string;
    sportId: string;
    // role -> list of team levels (empty list when sport-level link)
    rolesToTeams: Map<string, { role: string | null; teamLevels: string[]; sportLevel: boolean }>;
  }>;
  firstTeamLink?: { teamId: string; sportId: string };
  hasCurrentLink: boolean;
  lastFormerYear: number | null;
}

const noteRecency = (acc: MutableAccum, link: LinkedPerson): void => {
  if (link.endDate == null) {
    acc.hasCurrentLink = true;
  } else {
    const year = new Date(link.endDate).getUTCFullYear();
    acc.lastFormerYear = acc.lastFormerYear == null ? year : Math.max(acc.lastFormerYear, year);
  }
};

export const rollupPeople = (organization: OrganizationWithPeople): RolledUpPerson[] => {
  const accumByPersonId = new Map<string, MutableAccum>();

  const ensure = (link: LinkedPerson): MutableAccum => {
    const id = link.person.id;
    let acc = accumByPersonId.get(id);
    if (!acc) {
      acc = {
        person: link.person,
        isPrimary: false,
        hasOrgLink: false,
        hasSportLink: false,
        hasTeamLink: false,
        orgRoles: [],
        sports: new Map(),
        hasCurrentLink: false,
        lastFormerYear: null,
      };
      accumByPersonId.set(id, acc);
    }
    if (link.isPrimary) acc.isPrimary = true;
    return acc;
  };

  // Org-level links
  for (const link of organization.people ?? []) {
    const acc = ensure(link);
    noteRecency(acc, link);
    acc.hasOrgLink = true;
    acc.orgRoles.push({ role: link.role });
  }

  // Walk sports
  for (const sport of organization.sports ?? []) {
    if (!sport.isActive) continue;
    const sportName = formatSportLabel(sport.gender, sport.name);
    const sportId = sport.id;

    // Sport-level links
    for (const link of sport.people ?? []) {
      const acc = ensure(link);
      noteRecency(acc, link);
      acc.hasSportLink = true;
      let bucket = acc.sports.get(sportId);
      if (!bucket) {
        bucket = { sportName, sportId, rolesToTeams: new Map() };
        acc.sports.set(sportId, bucket);
      }
      const roleKey = `__sport__::${link.role ?? ''}`;
      if (!bucket.rolesToTeams.has(roleKey)) {
        bucket.rolesToTeams.set(roleKey, { role: link.role, teamLevels: [], sportLevel: true });
      }
    }

    // Team-level links
    for (const team of sport.teams ?? []) {
      if (!team.isActive) continue;
      for (const link of team.people ?? []) {
        const acc = ensure(link);
        noteRecency(acc, link);
        acc.hasTeamLink = true;
        if (!acc.firstTeamLink) acc.firstTeamLink = { teamId: team.id, sportId };
        let bucket = acc.sports.get(sportId);
        if (!bucket) {
          bucket = { sportName, sportId, rolesToTeams: new Map() };
          acc.sports.set(sportId, bucket);
        }
        const roleKey = `${link.role ?? ''}`;
        let roleEntry = bucket.rolesToTeams.get(roleKey);
        if (!roleEntry) {
          roleEntry = { role: link.role, teamLevels: [], sportLevel: false };
          bucket.rolesToTeams.set(roleKey, roleEntry);
        }
        if (!roleEntry.teamLevels.includes(team.level)) {
          roleEntry.teamLevels.push(team.level);
        }
      }
    }
  }

  const result: RolledUpPerson[] = [];
  for (const acc of accumByPersonId.values()) {
    if (!acc.hasOrgLink && !acc.hasSportLink && !acc.hasTeamLink) continue;

    const level: RollupLevel = acc.hasTeamLink ? 'TEAM' : acc.hasSportLink ? 'SPORT' : 'ORG';

    const displayLinks: RolledUpDisplayLink[] = [];
    if (acc.hasOrgLink) {
      displayLinks.push({
        scope: 'ORG',
        items: acc.orgRoles.map((r) => ({ role: r.role })),
      });
    }
    for (const bucket of acc.sports.values()) {
      const items: RolledUpDisplayLink['items'] = [];
      for (const r of bucket.rolesToTeams.values()) {
        items.push(r.sportLevel ? { role: r.role } : { role: r.role, teamLevels: r.teamLevels });
      }
      displayLinks.push({
        scope: 'SPORT',
        sportName: bucket.sportName,
        sportId: bucket.sportId,
        items,
      });
    }

    result.push({
      person: acc.person,
      isPrimary: acc.isPrimary,
      level,
      displayLinks,
      firstTeamLink: acc.firstTeamLink,
      isFormer: !acc.hasCurrentLink,
      lastSeasonYear: acc.hasCurrentLink ? null : acc.lastFormerYear,
    });
  }

  return result;
};

export interface PeopleFilters {
  query: string;
  sportId: string | null;
}

export const filterRolledUpPeople = (
  rolled: RolledUpPerson[],
  filters: PeopleFilters,
): RolledUpPerson[] => {
  const q = filters.query.trim().toLowerCase();
  return rolled.filter((r) => {
    if (filters.sportId) {
      const hasSport = r.displayLinks.some(
        (l) => l.scope === 'SPORT' && l.sportId === filters.sportId,
      );
      if (!hasSport) return false;
    }
    if (!q) return true;
    const haystack = [
      r.person.firstName,
      r.person.lastName,
      r.person.email ?? '',
      r.person.phone ?? '',
      ...r.displayLinks.flatMap((l) => l.items.map((i) => i.role ?? '')),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
};

export interface GroupedPeople {
  coaches: RolledUpPerson[];
  sportStaff: RolledUpPerson[];
  orgStaff: RolledUpPerson[];
}

export const groupAndSortRolledUpPeople = (rolled: RolledUpPerson[]): GroupedPeople => {
  const groups: GroupedPeople = { coaches: [], sportStaff: [], orgStaff: [] };
  for (const r of rolled) {
    if (r.level === 'TEAM') groups.coaches.push(r);
    else if (r.level === 'SPORT') groups.sportStaff.push(r);
    else groups.orgStaff.push(r);
  }
  const cmp = (a: RolledUpPerson, b: RolledUpPerson): number => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    const ln = a.person.lastName.toLowerCase().localeCompare(b.person.lastName.toLowerCase());
    if (ln !== 0) return ln;
    return a.person.firstName.toLowerCase().localeCompare(b.person.firstName.toLowerCase());
  };
  groups.coaches.sort(cmp);
  groups.sportStaff.sort(cmp);
  groups.orgStaff.sort(cmp);
  return groups;
};
